import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@astro/auth'
import './DashboardPage.css'
import { useDashboard, useAstrologerBookings, formatKarma } from './useDashboard'
import { useAstrologerOnboarding } from './useAstrologerOnboarding'
import DashboardIntro from './DashboardIntro'
import { TAB_COMPONENTS, PlaceholderTab, resolveTab } from './tabs'
import { normalizeRoles, primaryRole, canAccessTab, tabRole } from './roles'
import { BsGrid, BsHandbag, BsPersonGear, BsCurrencyRupee } from "react-icons/bs";
import { FaRegCircle, FaRegStar, FaDharmachakra } from "react-icons/fa";
import { IoChatbubbleOutline, IoSettingsOutline } from "react-icons/io5";
import { RiGraduationCapLine } from "react-icons/ri";
import { FiFileText } from "react-icons/fi";
import { HiOutlineArrowRightOnRectangle } from "react-icons/hi2";
import { BiSupport } from "react-icons/bi";
import { PiSealBold, PiFlowerLotus } from "react-icons/pi";
import { LuMenu, LuX, LuChevronDown, LuUserPlus, LuRadio, LuNotebookPen } from "react-icons/lu";


/* Sidebar nav — exact icons from the reference (heroicons-style strokes).
   Which entries a user actually sees is decided by roles.js (TAB_ACCESS):
   the union of the tabs granted to each of their roles. */
const NAV = [
  { key: 'overview', label: 'Dashboard', badge: null, icon: BsGrid, size: 20 },
  { key: 'horoscope', label: 'Horoscope', badge: null, icon: FaRegStar, size: 20 },
  { key: 'kundli', label: 'Kundli', badge: null, icon: FaRegCircle, size: 20 },
  // Appoinments badge is live (upcoming session count) — set in the render.
  { key: 'appoinments', label: 'Appoinments', badge: null, icon: IoChatbubbleOutline, size: 20 },
  { key: 'orders', label: 'Orders', badge: null, icon: BsHandbag, size: 20 },
  { key: 'pujabookings', label: 'Puja Bookings', badge: null, icon: PiFlowerLotus, size: 20 },
  { key: 'earnings', label: 'Earnings', badge: null, icon: BsCurrencyRupee, size: 20 },
  // Astrologer-only (roles.js): start an instant public live session.
  { key: 'golive', label: 'Go Live', badge: null, icon: LuRadio, size: 20 },
  // Astrologer-only (roles.js): write for the public blog, admin-reviewed.
  { key: 'blog', label: 'My Articles', badge: null, icon: LuNotebookPen, size: 20 },
  { key: 'courses', label: 'Academy', badge: null, icon: RiGraduationCapLine, size: 20 },
  { key: 'reports', label: 'Reports', badge: null, icon: FiFileText, size: 20 },
  { key: 'remedies', label: 'Remedies', badge: null, icon: PiSealBold, size: 20 },
  // Shown only when the admin has the prompt on for this account (and they
  // haven't applied yet) — see useAstrologerOnboarding / tabAllowed below.
  { key: 'becomeastrologer', label: 'Become an Astrologer', badge: null, icon: LuUserPlus, size: 20 },
  { key: 'myprofile', label: 'My Profile', badge: null, icon: BsPersonGear, size: 20 },
  // 'pricing' shows only for EXCLUSIVE_TABS_BY_EMAIL accounts (see below).
  { key: 'pricing', label: 'Settings', badge: null, icon: IoSettingsOutline, size: 20 },
  { key: 'support', label: 'Help & Support', badge: null, icon: BiSupport, size: 20 }
]


/* Render the screen for the selected sidebar key via the tabs/ registry —
   role variants first (TAB_ROLE_COMPONENTS), then the shared component;
   keys without a dedicated screen yet (Reports, Remedies, Earnings) fall
   back to the placeholder. Each tab resolves against the role that granted
   IT (tabRole), not the user's overall primary role — an astrologer+
   instructor gets the instructor Academy screen but the astrologer kundli
   studio. Tabs receive `roles`/`role` to branch content. */
function renderTab(tab, setTab, data, roles) {
  const role = tabRole(tab, roles)
  const Tab = resolveTab(tab, role)
  if (!Tab) return <PlaceholderTab label={NAV.find((n) => n.key === tab)?.label ?? 'Section'} />
  return <Tab data={data} onSwitch={setTab} roles={roles} role={role} />
}

/* Keys a ?tab= param may select: every sidebar entry plus any registered
   screen not (yet) in the sidebar — minus anything the user's roles don't
   grant. Anything else falls back to overview. */
const ALL_TAB_KEYS = [...new Set([...NAV.map((n) => n.key), ...Object.keys(TAB_COMPONENTS)])]

/* SPECIAL CASE (temporary, TODO refactor into roles.js / back-end config):
   per-account tab hiding. Lowercase email → tab keys to hide for that login,
   applied on top of the role-based TAB_ACCESS filter. Hidden keys drop out of
   BOTH the sidebar and validTabs, so ?tab= links and in-page onSwitch calls
   to them fall back to the overview tab instead of breaking. */
const HIDDEN_TABS_BY_EMAIL = {
  'rjn.bharti333@gmail.com': ['remedies', 'support', 'orders', 'pujabookings'],
}
const NO_HIDDEN_TABS = [] // stable identity for the memo deps below

/* SPECIAL CASE (temporary, TODO refactor with HIDDEN_TABS_BY_EMAIL): tabs
   visible ONLY to the listed logins — everyone else loses them from both
   the sidebar and validTabs (on top of the role-based TAB_ACCESS filter).
   Tab key → lowercase emails. */
const EXCLUSIVE_TABS_BY_EMAIL = {
  pricing: ['rjn.bharti333@gmail.com'],
}

/* ─────────────────────────── Page ─────────────────────────── */
/**
 * `stickyTop` is the host app's fixed-header height — where the desktop sidebar
 * pins. `mobileStickyTop` is the same measurement for the phone layout, where
 * the mobile section bar pins; host headers are routinely shorter there (the
 * website's drops from 130px to ~104px once the quick-links row goes away), and
 * reusing the desktop figure left the bar floating in a visible gap below the
 * navbar. Defaults to `stickyTop` for hosts whose header height doesn't change.
 */
export default function DashboardPage({
  heroTopClass = 'pt-12 lg:pt-16',
  stickyTop = '0px',
  mobileStickyTop = stickyTop,
}) {
  // The active tab lives in the URL (?tab=orders), not component state, so a
  // reload, a shared link, or a redirect from another app lands on the exact
  // screen. Selecting a tab pushes history, so the back button walks tabs.
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();

  // A user may hold several roles (e.g. astrologer + instructor); they get
  // the combined (union) tab list, and tabs branch content on the primary role.
  const roles = useMemo(() => normalizeRoles(user?.roles), [user])
  const role = useMemo(() => primaryRole(roles), [roles])
  const email = String(user?.email || '').toLowerCase()
  const hiddenTabs = HIDDEN_TABS_BY_EMAIL[email] ?? NO_HIDDEN_TABS
  // "Become an Astrologer" is admin-controlled per role AND per account, so the
  // answer comes from the API rather than from roles.js: the screen exists only
  // while this account is offered the prompt, or has an application to report.
  const {
    canApply: canApplyAsAstrologer,
    hasEntry: astrologerOnboardingEntry,
    application: astrologerApplication,
  } = useAstrologerOnboarding()
  const tabAllowed = useMemo(() => {
    return (key) =>
      canAccessTab(key, roles) &&
      !hiddenTabs.includes(key) &&
      (!EXCLUSIVE_TABS_BY_EMAIL[key] || EXCLUSIVE_TABS_BY_EMAIL[key].includes(email)) &&
      (key !== 'becomeastrologer' || astrologerOnboardingEntry)
  }, [roles, hiddenTabs, email, astrologerOnboardingEntry])
  const visibleNav = useMemo(
    () =>
      NAV.filter((n) => tabAllowed(n.key)).map((n) =>
        // Once the application is in, the entry is where its review state is
        // reported — "Become an Astrologer" would be asking for it twice.
        n.key === 'becomeastrologer' && astrologerApplication
          ? { ...n, label: 'Astrologer Application' }
          : n,
      ),
    [tabAllowed, astrologerApplication],
  )
  const validTabs = useMemo(() => new Set(ALL_TAB_KEYS.filter(tabAllowed)), [tabAllowed])

  const param = searchParams.get('tab')
  const tab = validTabs.has(param) ? param : 'overview'

  // Below lg the sidebar is an off-canvas drawer (see the render).
  const [navOpen, setNavOpen] = useState(false)

  const setTab = (key) => {
    const next = new URLSearchParams(searchParams)
    if (key === 'overview') next.delete('tab') // canonical URL for the default tab
    else next.set('tab', key)
    // Tab-scoped sub-view params reset on switch — landing on Academy from the
    // sidebar should show the course list, not a previously opened course.
    next.delete('course')
    next.delete('view') // Kundli's Studio / Saved Kundlis sub-tab
    setSearchParams(next)
    // Picking a section is the drawer's whole job — get out of the way so the
    // tap lands on content, not on a still-open overlay.
    setNavOpen(false)
  }

  // Escape closes the drawer, and the page behind it must not scroll while it
  // is open (otherwise a drag on the backdrop scrolls the tab underneath and
  // the user loses their place).
  useEffect(() => {
    if (!navOpen) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false) }
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previous
      window.removeEventListener('keydown', onKey)
    }
  }, [navOpen])

  // Crossing into the desktop layout must not leave the drawer state armed —
  // otherwise rotating a tablet back to portrait reopens it unprompted.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const onChange = (e) => { if (e.matches) setNavOpen(false) }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  const data = useDashboard(user)
  // Appoinments badge — live count of upcoming sessions. Astrologers count
  // the sessions booked WITH them (the hook only fetches for astrologer
  // accounts); everyone else counts their own bookings. `join` marks the
  // not-cancelled / not-completed rows.
  const { bookings: hostBookings } = useAstrologerBookings()
  const upcomingCount = (role === 'astrologer' ? hostBookings : data.consultations).filter((c) => c.join).length
  const firstName = (data.profile.name || 'Seeker').split(/\s+/)[0]
  // What the mobile bar names as the current section. Registered screens that
  // have no sidebar entry (opened via ?tab=) fall back to a readable label.
  const activeNavLabel =
    // visibleNav, not NAV: entries whose label depends on live state (the
    // astrologer application) are relabelled there.
    visibleNav.find((n) => n.key === tab)?.label ??
    String(tab).replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  // console.log('Dashboard data:', data)
  const today = useMemo(
    () => new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
    [],
  )
  // overflow-x must be `clip`, not `hidden`: a hidden ancestor becomes a
  // scroll container and disables the sidebar's position: sticky.
  return (
    <div
      className="dashboard-page text-astroDark antialiased overflow-x-clip"
      style={{ '--dash-sticky-top': stickyTop, '--dash-sticky-top-mobile': mobileStickyTop }}
    >
      {/* ── Dashboard hero banner — site chrome colours: astroDark base with a primary-maroon glow and gold accents, matching the header. ── */}
      <header className={`relative ${heroTopClass} pb-8 sm:pb-10 overflow-hidden bg-astroDark text-white border-b border-goldPrimary/15`}>
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute inset-0 bg-linear-to-br from-primary/40 via-transparent to-transparent" />
          <div className="absolute -top-20 -right-20 w-[40vw] h-[40vw] bg-cosmicRed/35 rounded-full animate-aura blur-3xl" />
          <div className="absolute -bottom-20 -left-10 w-[30vw] h-[30vw] bg-goldGlow/20 rounded-full animate-aura blur-3xl" style={{ animationDelay: '-3s' }} />
          <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] aspect-square opacity-[0.05] text-goldPrimary animate-spin-slow" viewBox="0 0 200 200" fill="none" stroke="currentColor">
            <circle cx="100" cy="100" r="98" strokeWidth="0.3" />
            <circle cx="100" cy="100" r="74" strokeWidth="0.3" strokeDasharray="2 4" />
          </svg>
        </div>
        {/* z-30 keeps the hero clickable: <main> below is pulled up over the
            header's bottom strip (-mt-16/-mt-20) at z-20, and its transparent
            padding would otherwise swallow clicks on these buttons. */}
        <div className="relative z-30 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 sm:gap-6">
            <div className="space-y-2 min-w-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <span className="hidden sm:block w-6 h-px bg-goldPrimary/50" />
                <span className="text-[8px] uppercase font-bold tracking-[0.3em] text-goldPrimary/90 font-royal">Seeker Portal</span>
                <span className="text-[9px] font-medium tracking-wider text-white/70 px-2 py-0.5 rounded-full border border-white/10 bg-white/4">🗓 {today}</span>
              </div>
              <h1 className="text-xl sm:text-3xl lg:text-4xl font-royal text-[#FBF3E4] leading-[1.2] wrap-break-word">
                Namaste, <span className="bg-linear-to-r from-goldPrimary via-amber-200 to-goldAccent bg-clip-text text-transparent font-semibold">{firstName}</span> 🙏
              </h1>
              <p className="text-[11px] sm:text-xs text-white/50 font-light max-w-sm">Mercury favours communication today. Review your upcoming consultation and explore your latest remedies.</p>
            </div>
            {/* Full-bleed side-by-side on phones (two equal taps), auto-width
                from md where they sit beside the greeting. */}
            <div className="flex items-stretch gap-2 shrink-0">
              {validTabs.has('appoinments') && (
                <button onClick={() => setTab('appoinments')} className="flex-1 md:flex-none bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.15em] px-4 sm:px-5 py-3 rounded-xl shadow-lg shadow-goldPrimary/10 hover:shadow-goldPrimary/25 transition-all hover:-translate-y-0.5">Consultation</button>
              )}
              {validTabs.has('kundli') && (
                <button onClick={() => setTab('kundli')} className="flex-1 md:flex-none border border-white/10 hover:border-goldPrimary/50 text-[#FBF3E4] hover:text-goldPrimary font-semibold text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.15em] px-4 sm:px-5 py-3 rounded-xl transition-all hover:bg-white/3">View Kundli</button>
              )}
              {/* Admin-controlled CTA (Settings → Feature Visibility): only for
                  the accounts allowed to apply, and only until they have. */}
              {canApplyAsAstrologer && validTabs.has('becomeastrologer') && (
                <button onClick={() => setTab('becomeastrologer')} className="flex-1 md:flex-none border border-goldPrimary/40 bg-goldPrimary/10 hover:bg-goldPrimary/20 text-goldPrimary font-semibold text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.15em] px-4 sm:px-5 py-3 rounded-xl transition-all">Become an Astrologer</button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Main portal layout ──
          md:px-12 is deliberate desktop inset (it stacks with the inner
          container's own padding) and is left exactly as it was — it never
          applied below md, so it was never a mobile problem. Only the vertical
          rhythm is scaled down here: the negative top margin pulls this over
          the hero's bottom strip, so the top padding has to exceed it, and
          both were sized for desktop. */}
      <main className="relative z-20 -mt-12 sm:-mt-16 lg:-mt-20 pb-16 sm:pb-24 pt-20 sm:pt-24 lg:pt-28 md:px-12">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12">
          {/* Intro text block — opens the body content, directly under the banner. */}
          <DashboardIntro />
          {/* Mobile section switcher. Below lg the sidebar is a drawer, so this
              bar is the only always-visible way to change tab — it pins flush
              beneath the host app's fixed header (--dash-sticky-top-mobile) so
              it stays reachable however far the tab scrolls. z-40 keeps it
              under the host header (website: z-50) and under the drawer, but
              over the tab content. */}
          <div
            className="dash-mobilebar lg:hidden sticky z-40 -mx-4 sm:-mx-6 mb-4 px-4 sm:px-6 py-2.5 bg-[#fdfaf5]/95 backdrop-blur-sm border-b border-stone-200"
            style={{ top: 'var(--dash-sticky-top-mobile, var(--dash-sticky-top, 0px))' }}
          >
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setNavOpen(true)}
                aria-expanded={navOpen}
                aria-controls="dash-mobile-nav"
                className="flex items-center gap-2.5 min-w-0 flex-1 p-2 pr-3 rounded-xl bg-gradient-to-r from-[#3D0A0A] to-[#5C1212] border border-[#D4A017]/50 shadow-md shadow-[#3D0A0A]/25 active:scale-[0.99] transition-transform text-left"
              >
                <span className="shrink-0 w-9 h-9 rounded-lg bg-[#D4A017] flex items-center justify-center shadow-inner">
                  <LuMenu size={17} className="text-[#3D0A0A]" strokeWidth={2.5} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[9px] font-semibold uppercase tracking-[0.2em] text-[#D4A017] leading-none">Menu · Section</span>
                  <span className="block truncate text-sm font-bold text-[#FFFBF7] leading-tight mt-1">{activeNavLabel}</span>
                </span>
                {upcomingCount ? (
                  <span className="shrink-0 bg-[#D4A017] text-[#3D0A0A] text-[10px] font-bold px-2 py-0.5 rounded-full">{upcomingCount}</span>
                ) : null}
                <LuChevronDown size={17} className="shrink-0 text-[#D4A017]" strokeWidth={2.5} />
              </button>
              {data.avatarUrl ? (
                <img src={data.avatarUrl} alt={data.profile.name} className="shrink-0 w-11 h-11 rounded-xl object-cover object-top bg-[#3D0A0A]" />
              ) : (
                <div className="shrink-0 w-11 h-11 rounded-xl bg-[#3D0A0A] flex items-center justify-center text-[#D4A017] font-bold text-sm">{data.initials}</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Sidebar — the in-grid column exists on lg+ only. Below that the
                same content rides the drawer below, so the mobile view opens on
                the CONTENT instead of making users scroll past a profile card
                and thirteen nav rows to reach it. */}
            <aside className="dash-sidebar hidden lg:block bg-[#FFFBF7] p-6 border border-stone-200 lg:col-span-3 h-max">
              <SidebarContent
                data={data}
                visibleNav={visibleNav}
                tab={tab}
                setTab={setTab}
                upcomingCount={upcomingCount}
                logout={logout}
              />
            </aside>

            {/* Content */}
            <section className="lg:col-span-9 space-y-6 min-w-0">
              {renderTab(tab, setTab, data, roles)}
            </section>
          </div>
        </div>

        {/* ── Mobile nav drawer (below lg) ──
            PORTALED to <body>, exactly as the tabs' dialogs are. Rendered in
            place it sat inside <main class="relative z-20">, whose own z-index
            opens a stacking context — the drawer's z-[1200] then only competed
            with main's descendants, so the hero header (a SIBLING of main at
            z-30) and the host app's fixed navbar (z-50) both painted straight
            over it. From <body> it shares the root stacking context, where
            z-[1200] genuinely outranks the navbar and the suite's fixed bottom
            tab bar (#mn-nav, z-index 100). */}
        {navOpen && typeof document !== 'undefined' && createPortal(
          <div className="dash-drawer-root lg:hidden fixed inset-0 z-[1200]" role="dialog" aria-modal="true" aria-label="Dashboard sections">
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setNavOpen(false)}
              className="absolute inset-0 w-full h-full bg-black/50 backdrop-blur-[2px] animate-[dash-fade-in_.2s_ease-out]"
            />
            <div
              id="dash-mobile-nav"
              className="dash-drawer absolute inset-y-0 left-0 w-[86%] max-w-xs bg-[#FFFBF7] border-r border-stone-200 shadow-2xl overflow-y-auto overscroll-contain"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 bg-[#FFFBF7] border-b border-stone-200">
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#A67C00]">Menu</span>
                <button
                  type="button"
                  onClick={() => setNavOpen(false)}
                  aria-label="Close menu"
                  className="w-9 h-9 rounded-full flex items-center justify-center text-[#3D0A0A] hover:bg-stone-100"
                >
                  <LuX size={18} />
                </button>
              </div>
              <div className="p-5">
                <SidebarContent
                  data={data}
                  visibleNav={visibleNav}
                  tab={tab}
                  setTab={setTab}
                  upcomingCount={upcomingCount}
                  logout={logout}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
      </main>
    </div>
  )
}

/**
 * The sidebar's contents — profile card, karma stats, nav, logout. Rendered
 * twice on purpose: once in the lg+ in-grid column, once in the mobile drawer.
 * Sharing one component (rather than one element repositioned by breakpoint
 * classes) is what lets the drawer be `fixed` while the desktop column stays
 * `sticky` inside the grid — a single node cannot be both.
 */
function SidebarContent({ data, visibleNav, tab, setTab, upcomingCount, logout }) {
  return (
    <>
      <div className="flex flex-col items-center text-center pb-6 border-b border-stone-200">
        <div className="relative">
          {data.avatarUrl ? (
            <img src={data.avatarUrl} alt={data.profile.name} className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover object-top bg-[#3D0A0A]" />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-[#3D0A0A] flex items-center justify-center text-[#D4A017] font-bold text-2xl sm:text-3xl">{data.initials}</div>
          )}
          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-600 border-4 border-[#FFFBF7] rounded-full" />
        </div>
        <h3 className="mt-3 text-lg sm:text-xl font-bold text-[#3D0A0A] wrap-break-word max-w-full">{data.profile.name}</h3>
        <span className="mt-2 px-4 py-1 bg-[#F5E6D3] text-[#A67C00] text-xs font-bold rounded-full tracking-wider">{data.membership}</span>
      </div>

      <div className="grid grid-cols-2 gap-2 py-5 sm:py-6 border-b border-stone-200 text-center">
        <div>
          <FaDharmachakra className="w-6 h-6 mx-auto text-[#A67C00]" />
          <p className="text-[10px] text-stone-500 mt-1 uppercase">Karma Points</p>
          <p className="font-bold text-[#3D0A0A]">{data.profileLoading ? '—' : formatKarma(data.karmaPoints ?? 0)}</p>
        </div>
        <div>
          <svg className="w-6 h-6 mx-auto text-[#A67C00]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
          <p className="text-[10px] text-stone-500 mt-1 uppercase">Spiritual Level</p>
          <p className="font-bold text-[#3D0A0A]">7</p>
          <p className="text-[9px] text-stone-500">Sadhak</p>
        </div>
      </div>

      <nav className="py-4 space-y-1">
        {visibleNav.map((item) => {
          const active = item.key === tab
          const Icon = item.icon
          const badge = item.key === 'appoinments' ? (upcomingCount || null) : item.badge
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`side-link w-full flex items-center justify-between gap-4 p-3 rounded-xl font-bold ${active ? 'bg-[#3D0A0A] text-white' : 'text-[#3D0A0A] hover:bg-stone-100'}`}
            >
              {/* min-w-0 + truncate are safety only — at the drawer's 280px of
                  usable width every label still fits, so desktop is unchanged. */}
              <span className="flex items-center gap-4 min-w-0">
                <Icon size={item.size} className="shrink-0" />
                <span className="truncate">{item.label}</span>
              </span>
              {badge ? <span className="shrink-0 bg-[#A67C00] text-white text-[10px] px-2 py-0.5 rounded-full">{badge}</span> : null}
            </button>
          )
        })}
      </nav>

      <div className="mt-4 sm:mt-6 pt-4 border-t border-stone-200">
        <button type="button" onClick={() => logout()} className="w-full flex items-center gap-4 p-3 text-[#3D0A0A] font-bold hover:bg-stone-100 rounded-xl">
          <HiOutlineArrowRightOnRectangle size={20} className="shrink-0" />
          Logout
        </button>
      </div>
    </>
  )
}
